import axios from "axios";

type RequestParams = Record<string, unknown>;

type RequestHeaders = Record<string, string>;

export type RequestOptions = {
    "params"?:  RequestParams;
    "headers"?: RequestHeaders;
    "timeout"?: number;
};

let msgpackDecode: ( ( data: ArrayBuffer ) => unknown ) | null = null;

/**
 * Loads the MessagePack decoder. Required for realtime API calls.
 *
 * @returns A promise that resolves once decoder initialization is attempted.
 * @throws {Error} When `@msgpack/msgpack` cannot be loaded.
 */
export async function ensureMsgpackDecoder(): Promise<void> {
    if ( msgpackDecode !== null ) {
        return;
    }

    try {
        const mod = await import( "@msgpack/msgpack" );
        msgpackDecode = ( data: ArrayBuffer ) => mod.decode( new Uint8Array( data ) );
    } catch( cause ) {
        throw new Error( "MessagePack decoder unavailable; realtime API requires @msgpack/msgpack", { "cause": cause } );
    }
}

/**
 * Performs a typed JSON GET request.
 *
 * @param url The target endpoint URL.
 * @param options Optional request options (query params, headers, timeout).
 * @returns The parsed JSON payload typed as `T`.
 */
export async function requestGetJson<T>( url: string, options?: RequestOptions ): Promise<T> {
    const response = await axios.get<T>( url, {
        "params":  options?.params,
        "headers": options?.headers,
        "timeout": options?.timeout,
    } );

    return response.data;
}

/**
 * Performs a typed JSON POST request.
 *
 * @param url The target endpoint URL.
 * @param body The JSON-serializable payload to send.
 * @param options Optional request options (query params, headers, timeout).
 * @returns The parsed JSON payload typed as `T`.
 */
export async function requestPostJson<T>( url: string, body: unknown, options?: RequestOptions ): Promise<T> {
    const response = await axios.post<T>( url, body, {
        "params":  options?.params,
        "headers": options?.headers,
        "timeout": options?.timeout,
    } );

    return response.data;
}

/**
 * Performs a typed JSON DELETE request.
 *
 * @param url The target endpoint URL.
 * @param options Optional request options (query params, headers, timeout).
 * @returns The parsed JSON payload typed as `T`.
 */
export async function requestDeleteJson<T>( url: string, options?: RequestOptions ): Promise<T> {
    const response = await axios.delete<T>( url, {
        "params":  options?.params,
        "headers": options?.headers,
        "timeout": options?.timeout,
    } );

    return response.data;
}

/**
 * Performs a GET request and decodes the response as MessagePack.
 *
 * Sends `Accept: application/msgpack` and expects a binary body. No JSON fallback.
 *
 * @param url The target endpoint URL.
 * @param options Optional request options (query params, headers, timeout).
 * @returns The decoded payload typed as `T`.
 */
export async function requestGetMsgpack<T>( url: string, options?: RequestOptions ): Promise<T> {
    await ensureMsgpackDecoder();

    const response = await axios.get<ArrayBuffer>( url, {
        "params":       options?.params,
        "timeout":      options?.timeout,
        "responseType": "arraybuffer",
        "headers":      {
            ...options?.headers ?? {},
            "Accept": "application/msgpack",
        },
    } );

    return msgpackDecode!( response.data ) as T;
}

/**
 * Performs a POST request and decodes the response as MessagePack.
 *
 * Sends `Accept: application/msgpack` and expects a binary body. No JSON fallback.
 *
 * @param url The target endpoint URL.
 * @param body The JSON-serializable request body.
 * @param options Optional request options (query params, headers, timeout).
 * @returns The decoded payload typed as `T`.
 */
export async function requestPostMsgpack<T>(
    url: string,
    body: unknown,
    options?: RequestOptions,
): Promise<T> {
    await ensureMsgpackDecoder();

    const response = await axios.post<ArrayBuffer>( url, body, {
        "params":       options?.params,
        "timeout":      options?.timeout,
        "responseType": "arraybuffer",
        "headers":      {
            ...options?.headers ?? {},
            "Accept":       "application/msgpack",
            "Content-Type": "application/json",
        },
    } );

    return msgpackDecode!( response.data ) as T;
}
